import React, { useState, useEffect, useCallback } from 'react';
import './AdminCommon.css';
import { getAllUsers, deleteUser } from '../../services/userApi';
import { Trash2, Search, X } from 'lucide-react';

function AdminUsers({ idToken }) {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterAdmin, setFilterAdmin] = useState('all'); // all, admin, user
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAllUsers(idToken);
            setUsers(data);
        } catch (err) {
            setError('Erreur lors du chargement des utilisateurs: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [idToken]);

    const filterUsersList = useCallback(() => {
        let filtered = users;

        // Filter by search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(user =>
                user.name?.toLowerCase().includes(term) ||
                user.surname?.toLowerCase().includes(term) ||
                user.email?.toLowerCase().includes(term) ||
                user.phone?.includes(term)
            );
        }

        // Filter by admin status
        if (filterAdmin === 'admin') {
            filtered = filtered.filter(user => user.isAdmin);
        } else if (filterAdmin === 'user') {
            filtered = filtered.filter(user => !user.isAdmin);
        }

        setFilteredUsers(filtered);
    }, [users, searchTerm, filterAdmin]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        filterUsersList();
    }, [filterUsersList]);

    const handleDelete = async (userId) => {
        try {
            await deleteUser(userId);
            setUsers((prev) => prev.filter((u) => u.id !== userId));
            setDeleteConfirm(null);
            alert('Utilisateur supprimé avec succès');
        } catch (err) {
            alert('Erreur lors de la suppression: ' + err.message);
        }
    };

    if (loading) {
        return <div className="admin-loading">Chargement des utilisateurs...</div>;
    }

    if (error) {
        return <div className="admin-error">{error}</div>;
    }

    return (
        <div className="admin-common-container">
            <div className="admin-common-header">
                <h2>Gestion des utilisateurs</h2>
                <div className="admin-stats">
                    <span className="admin-stat-item">Total: {users.length}</span>
                    <span className="admin-stat-item">Admins: {users.filter(u => u.isAdmin).length}</span>
                    <span className="admin-stat-item">Clients: {users.filter(u => !u.isAdmin).length}</span>
                </div>
            </div>

            <div className="admin-filters">
                <div className="admin-search-box">
                    <Search size={20} />
                    <input
                        type="text"
                        placeholder="Rechercher par nom, email, téléphone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <X 
                            size={20} 
                            className="search-clear" 
                            onClick={() => setSearchTerm('')}
                        />
                    )}
                </div>

                <div className="admin-filter-buttons">
                    <button 
                        className={`filter-btn ${filterAdmin === 'all' ? 'active' : ''}`}
                        onClick={() => setFilterAdmin('all')}
                    >
                        Tous
                    </button>
                    <button 
                        className={`filter-btn ${filterAdmin === 'admin' ? 'active' : ''}`}
                        onClick={() => setFilterAdmin('admin')}
                    >
                        Administrateurs
                    </button>
                    <button 
                        className={`filter-btn ${filterAdmin === 'user' ? 'active' : ''}`}
                        onClick={() => setFilterAdmin('user')}
                    >
                        Clients
                    </button>
                </div>
            </div>

            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Email</th>
                            <th>Téléphone</th>
                            <th>Adresse</th>
                            <th>Rôle</th>
                            <th>Créé le</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="no-results">
                                    Aucun utilisateur trouvé
                                </td>
                            </tr>
                        ) : (
                            filteredUsers.map(user => (
                                <tr key={user.id}>
                                    <td>
                                        <strong>{user.name} {user.surname}</strong>
                                    </td>
                                    <td>{user.email}</td>
                                    <td>{user.phone || '-'}</td>
                                    <td>
                                        {user.address ? 
                                            `${user.address.city}, ${user.address.country}` 
                                            : '-'
                                        }
                                    </td>
                                    <td>
                                        <span className={`role-badge ${user.isAdmin ? 'admin' : 'user'}`}>
                                            {user.isAdmin ? 'Admin' : 'Client'}
                                        </span>
                                    </td>
                                    <td>
                                        {new Date(user.dateCreated).toLocaleDateString('fr-CH')}
                                    </td>
                                    <td>
                                        <button
                                            className="action-btn delete-btn"
                                            onClick={() => setDeleteConfirm(user.id)}
                                            title="Supprimer"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {deleteConfirm && (
                <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Confirmer la suppression</h3>
                        <p>Êtes-vous sûr de vouloir supprimer cet utilisateur ?</p>
                        <p className="warning-text">Cette action est irréversible.</p>
                        <div className="modal-actions">
                            <button 
                                className="modal-btn cancel-btn"
                                onClick={() => setDeleteConfirm(null)}
                            >
                                Annuler
                            </button>
                            <button 
                                className="modal-btn confirm-btn"
                                onClick={() => handleDelete(deleteConfirm)}
                            >
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminUsers;
